import { Renderer } from '@unseenco/taxi'
import { Splide }  from '@splidejs/splide'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger.js'
import List from 'list.js'

import Player from '@vimeo/player'
import { animate, stagger, createTimeline, cubicBezier, utils } from 'animejs';

// --- Shared constants ---

const EASE = cubicBezier(0.358, 0.002, 0, 1);
const MOBILE_BREAKPOINT = 600;


// --- Shared helpers ---
function updateBodyClass(page) {
  const bodyClass = page.querySelector('[data-body-class]');
  if (!bodyClass) return;
  document.body.className = bodyClass.dataset.bodyClass;
}

function setManualScrollRestoration() {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }
}

function showWorkHeader(activeHref) {
  const header = document.querySelector('.work-grid-header');
  if (!header) return;

  header.querySelectorAll('.work-nav a').forEach(a => {
    a.classList.toggle('active', a.getAttribute('href') === activeHref);
  });

  const form = header.querySelector('form');
  const isInitialShow = header.style.display === 'none';
  const isListPage = activeHref === '/work/list/';

  if (isInitialShow) {
    // Header coming from hidden: set form state immediately, fade the whole header in
    if (form) form.style.visibility = isListPage ? 'hidden' : '';
    header.style.display = '';
    animate(header, { opacity: [0, 1], duration: 400, easing: EASE });
  } else {
    // Header already visible (work <-> work-list): transition handled form fade-out,
    // so just set visibility and animate form back in when returning to grid
    if (form) {
      if (isListPage) {
        form.style.visibility = 'hidden';
        form.style.opacity = '';
      } else {
        form.style.visibility = '';
        // transition handles fade-in
      }
    }
  }
}

function copyToClipboard(text) {
  const execFallback = () => {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  };

  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => {
      execFallback();
    });
  }

  execFallback();
  return Promise.resolve();
}

function sitewideInit() {
  document.querySelectorAll('.js-copy-email').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      const link = e.currentTarget;
      const original = link.textContent;
      copyToClipboard(link.dataset.email);
      link.textContent = 'Copied!';
      setTimeout(() => { link.textContent = original; }, 2000);
    });
  });

  document.querySelectorAll('.js-hp-copy-email').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      const img = e.currentTarget.querySelector('img');
      const originalSrc = img ? img.src : null;
      copyToClipboard(e.currentTarget.dataset.email);
      if (img) img.src = img.src.replace('card-email.png', 'card-email-copied.png');
      setTimeout(() => { if (img && originalSrc) img.src = originalSrc; }, 2000);
    });
  });
}

function buildSeamlessLoop(items, spacing) {
	let overlap = Math.ceil(1 / spacing), // number of EXTRA animations on either side of the start/end to accommodate the seamless looping
		startTime = 2 * items.length * spacing + 0.5, // center card 0 on load regardless of list length
		loopTime = (items.length + overlap) * spacing + 1, // the spot at the end where we loop back to the startTime 
		rawSequence = gsap.timeline({paused: true}), // this is where all the "real" animations live
		seamlessLoop = gsap.timeline({ // this merely scrubs the playhead of the rawSequence so that it appears to seamlessly loop
			paused: true,
			repeat: -1, // to accommodate infinite scrolling/looping
			onRepeat() { // works around a super rare edge case bug that's fixed GSAP 3.6.1
				this._time === this._dur && (this._tTime += this._dur - 0.01);
			}
		}),
		l = items.length + overlap * 2,
		time = 0,
		i, index, item;

	// set initial state of items
	gsap.set(items, {yPercent: 300, opacity: 1,	scale: 0});

	// now loop through and create all the animations in a staggered fashion. Remember, we must create EXTRA animations at the end to accommodate the seamless looping.
	for (i = 0; i < l; i++) {
		index = i % items.length;
		item = items[index];
		time = i * spacing;
		rawSequence
      .fromTo(item, {
        scale: 0.5, 
        opacity: 1
      }, {
        scale: 1, 
        opacity: 1, 
        zIndex: 100, 
        duration: 0.5, 
        yoyo: true, 
        repeat: 1, 
        ease: "power1.in", 
        immediateRender: false}, 
      time)
		  .fromTo(item, {
        yPercent: 300
      }, {
        yPercent: -300, 
        duration: 1, 
        ease: "none", 
        immediateRender: false
      }, time);
      
		i <= items.length && seamlessLoop.add("label" + i, time); // we don't really need these, but if you wanted to jump to key spots using labels, here ya go.
	}
	
	// here's where we set up the scrubbing of the playhead to make it appear seamless. 
	rawSequence.time(startTime);
	seamlessLoop.to(rawSequence, {
		time: loopTime,
		duration: loopTime - startTime,
		ease: "none"
	}).fromTo(rawSequence, {time: overlap * spacing + 1}, {
		time: startTime,
		duration: startTime - (overlap * spacing + 1),
		immediateRender: false,
		ease: "none"
	});
	return seamlessLoop;
}


function runHomepageAnimation() {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  
  const hpDelay = 700;
  const hpDuration = 1000;

  if (isMobile) {
    createTimeline()
    .add('.js-animated-header', {
      opacity: [0, 1],
      // delay: stagger(100),
      ease: EASE,
      duration: hpDuration,
    }, 500)
    .add('.js-animated-gallery', {
      opacity: [0, 1],
      y: [60, 0],
      // scale: [0.8, 1],
      // delay: stagger(200),
      ease: EASE,
      duration: hpDuration,
    }, 600)
    .add('.js-animated-about', {
      opacity: [0, 1],
      y: [100, 0],
      scale: [0.9, 1],
      delay: stagger(100),
      ease: EASE,
      duration: hpDuration,
    }, 1200)
  } else {
    createTimeline()
    .add('.js-animated-header', {
      opacity: [0, 1],
      delay: stagger(800),
      ease: EASE,
      duration: hpDuration,
    }, 500)
    .add('.js-animated-gallery', {
      opacity: [0, 1],
      y: [100, 0],
      scale: [0.8, 1],
      // delay: stagger(200),
      ease: EASE,
      duration: hpDuration,
    }, 1200)
    .add('.js-animated-about', {
      opacity: [0, 1],
      y: [100, 0],
      scale: [0.9, 1],
      delay: stagger(100),
      ease: EASE,
      duration: hpDuration,
    }, 1200)
  }

  
  
  /*
  .add('.gallery', {
    opacity: [0, 1],
    y: [300, 0],
    duration: hpDuration,
  }, 1000)
  */
  /*
  .add('.frame-strip.top', {
    translateY: [0, frameTopTo],
    duration: hpDuration,
    delay: hpDelay,
    onComplete: () => setTimeout(() => {
      const el = document.querySelector('.frame-strip.top');
      el.removeAttribute('style');
      el.classList.add('is-complete');
    }, 250),
  }, 1000)
  .add('.frame-strip.left',   { translateX: [-25, 0], duration: hpDuration, delay: hpDelay, easing: EASE }, 1000)
  .add('.frame-strip.right',  { translateX: [25, 0],  duration: hpDuration, delay: hpDelay, easing: EASE }, 1000)
  .add('.frame-strip.bottom', { translateY: [0, -25], duration: hpDuration, delay: hpDelay, easing: EASE }, 1000)
  .add('.loading-screen', {
    opacity: [1, 0],
    duration: 400,
    delay: hpDelay,
    easing: EASE,
    onComplete: () => {
      document.querySelector('.loading-screen').style.visibility = 'hidden';
    },
  }, 800)
  .add('.js-animated-header', { opacity: [0, 1], duration: hpDuration, delay: hpDelay, easing: EASE }, 1200)
  .add('.hp-card',            { opacity: [0, 1], scale: [0.62, 1], duration: 500, delay: 700, easing: EASE }, 1100);
  */
}


/* *******************************************************
 * DEFAULT
 * ******************************************************* */
export class DefaultRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted();
  }

  onEnter() {
    updateBodyClass(this.page);
    setManualScrollRestoration();
    sitewideInit();
  }
}

/* *******************************************************
 * ARCHIVE PAGE
 * ******************************************************* */
export class ArchiveRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    updateBodyClass(this.page);
    setManualScrollRestoration();
    sitewideInit();

    const featuredImg = document.querySelector('.archive-featured-image img');
    const featuredContainer = document.querySelector('.archive-featured-image');
    const firstItem = document.querySelector('.js-featured-archive-work li[data-image]');
    let activeVideoUrl = firstItem?.querySelector('.js-play-video')?.dataset.videoUrl ?? null;

    if (featuredImg) {
      document.querySelectorAll('.js-featured-archive-work li[data-image], .js-all-archive-work li[data-image]').forEach(li => {
        li.addEventListener('mouseenter', () => {
          featuredImg.src = li.dataset.image;
          activeVideoUrl = li.querySelector('.js-play-video')?.dataset.videoUrl ?? null;
        });
      });
    }

    if (featuredContainer) {
      featuredContainer.addEventListener('click', (e) => {
        e.preventDefault();
        if (!activeVideoUrl) return;
        const player = document.getElementById('vimeo-player');
        const popup = document.getElementById('video-popup');
        if (player && popup) {
          player.src = activeVideoUrl;
          popup.classList.add('visible');
        }
      });
    }

    initVideos();
  }

  onLeave() {
    cleanupVideos();
  }
}


/* *******************************************************
 * HOME PAGE V2
 * ******************************************************* */
export class HomepageRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    updateBodyClass(this.page);
    setManualScrollRestoration();
    sitewideInit();

    // homepage rolodex
    gsap.registerPlugin(ScrollTrigger);

    if (!window.isTaxiTransition) {
      runHomepageAnimation();
    } else {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  
      const hpDelay = 700;
      const hpDuration = 1000;

      if (isMobile) {
        createTimeline()
        .add('.js-animated-gallery', {
          opacity: [0, 1],
          y: [60, 0],
          // scale: [0.8, 1],
          // delay: stagger(200),
          ease: EASE,
          duration: hpDuration,
        }, 200)
        .add('.js-animated-about', {
          opacity: [0, 1],
          y: [100, 0],
          scale: [0.9, 1],
          delay: stagger(100),
          ease: EASE,
          duration: hpDuration,
        }, 300)
      } else {
        createTimeline()
        .add('.js-animated-gallery', {
          opacity: [0, 1],
          y: [100, 0],
          scale: [0.8, 1],
          // delay: stagger(200),
          ease: EASE,
          duration: hpDuration,
        }, 200)
        .add('.js-animated-about', {
          opacity: [0, 1],
          y: [100, 0],
          scale: [0.9, 1],
          delay: stagger(100),
          ease: EASE,
          duration: hpDuration,
        }, 300)
      }
    }

    document.querySelectorAll('.hp-scroll-item').forEach(item => {
      const video = item.querySelector('video');
      if (!video) return;
      video.style.opacity = '0';
      item.addEventListener('mouseenter', () => {
        video.style.opacity = '1';
        video.play();
      });
      item.addEventListener('mouseleave', () => {
        video.style.opacity = '0';
        video.pause();
        video.currentTime = 0;
      });
    });

    const spacing = 0.1,
      cards = gsap.utils.toArray('.cards li'),
      seamlessLoop = buildSeamlessLoop(cards, spacing);

    seamlessLoop.totalTime(seamlessLoop.duration() * 1); // sets time.
    let currentTime = seamlessLoop.totalTime();
    let lastDelta = 0;
    let snapTimer;

    function drive(delta) {
      lastDelta = delta;
      currentTime += delta;
      gsap.to(seamlessLoop, {
        totalTime: currentTime,
        duration: 0.5,
        ease: "power3",
        overwrite: true
      });

      clearTimeout(snapTimer);
      snapTimer = setTimeout(() => {
        const snapped = lastDelta >= 0
          ? Math.ceil(currentTime / spacing) * spacing
          : Math.floor(currentTime / spacing) * spacing;
        currentTime = snapped;
        gsap.to(seamlessLoop, {
          totalTime: snapped,
          duration: 0.4,
          ease: "power3.out",
          overwrite: true
        });
      }, 100);
    }

    // Desktop: mouse wheel
    window.addEventListener('wheel', (e) => {
      drive(e.deltaY * 0.001);
    }, { passive: true });

    // Mobile: touch
    let touchY = 0;
    window.addEventListener('touchstart', (e) => {
      touchY = e.touches[0].clientY;
    }, { passive: true });

    window.addEventListener('touchmove', (e) => {
      drive((touchY - e.touches[0].clientY) * 0.003);
      touchY = e.touches[0].clientY;
    }, { passive: true });

    /*
    gsap.registerPlugin(ScrollTrigger);
    
    const containerOffset = document.querySelector('.hp-scroll').getBoundingClientRect().top - 1;
    const cards = gsap.utils.toArray('.hp-scroll-item');

    this._triggers = cards.flatMap((card, i) => {
      const isLast = i === cards.length - 1;

      // Card 0 is already at containerOffset on load — pin it immediately
      const pin = ScrollTrigger.create({
        trigger: card,
        start: `top ${containerOffset}px`,
        end: `bottom ${containerOffset}px`,
        // start: 'top 50',
        // end: 'bottom 50',
        pin: true,
        pinSpacing: false,
        // markers: true,
        onLeave:     () => gsap.set(card, { opacity: 0 }),
        onEnterBack: () => gsap.set(card, { opacity: 1 }),
      });

      if (isLast) return [pin];

      // Card 0 has no scroll distance to drive its own scrub —
      // use card 1 as the trigger so the scale fires as card 1 scrolls in
      const scaleTrigger = card;

      const scaleAnim = gsap.fromTo(card,
        {
          scale: 1,
          yPercent: 0
        },
        {
          scale: 0.6,
          yPercent: 20,
          ease: 'none',
          scrollTrigger: {
            trigger: scaleTrigger,
            start: `top ${containerOffset}px`,
            end: `bottom ${containerOffset}px`,
            // start: 'top 50',
            // end: 'bottom 50',
            scrub: true,
           // markers:true,
          },
        }
      );

      const scaleAnim2 = gsap.fromTo(card,
        {
          scale: 1,
          yPercent: 0
        },
        {
          scale: 0.6,
          yPercent: 20,
          ease: 'none',
          scrollTrigger: {
            trigger: scaleTrigger,
            start: `top ${containerOffset}px`,
            end: `bottom ${containerOffset}px`,
            // start: 'top 50',
            // end: 'bottom 50',
            scrub: true,
           markers:true,
          },
        }
      );

      return [pin, scaleAnim.scrollTrigger, scaleAnim2.scrollTrigger];
    });
    */
  }

  onLeave() {
    
    this._triggers?.forEach(t => t.kill());
    this._triggers = null;
    
  }
}

/* *******************************************************
 * WORK GRID PAGE
 * ******************************************************* */
export class WorkGridRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    updateBodyClass(this.page);
    setManualScrollRestoration();
    sitewideInit();
    showWorkHeader('/work/');

    const gridListEl = document.getElementById('work-grid-list');
    if (gridListEl) {
      this._list = new List(gridListEl, {
        listClass: 'work-grid',
        valueNames: ['work-client', 'work-project'],
      });

      const searchInput = document.querySelector('.work-grid-header form input');
      if (searchInput) {
        searchInput.value = '';
        this._searchHandler = (e) => this._list.search(e.target.value);
        searchInput.addEventListener('input', this._searchHandler);
      }
    }

    this._observer = new IntersectionObserver((entries) => {
      const incoming = entries
        .filter(e => e.isIntersecting && !e.target.classList.contains('is-visible'))
        .map(e => e.target);

      if (incoming.length) {
        animateCards(incoming);
        incoming.forEach(card => this._observer.unobserve(card));
      }
    }, {
      threshold: 0,
      rootMargin: '0px 0px 140px 0px'
    });

    document.querySelectorAll('.work-grid-item').forEach(card => {
      this._observer.observe(card);

      const video = card.querySelector('video');
      if (!video) return;
      card.addEventListener('mouseenter', () => video.play());
      card.addEventListener('mouseleave', () => video.pause());
    });
  }

  onLeave() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    if (this._searchHandler) {
      const searchInput = document.querySelector('.work-grid-header form input');
      searchInput?.removeEventListener('input', this._searchHandler);
      this._searchHandler = null;
    }

    this._list = null;
  }
}


/* *******************************************************
 * WORK LIST PAGE
 * ******************************************************* */
export class WorkListRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    showWorkHeader('/work/list/');
    sitewideInit();

    this._listObserver = new IntersectionObserver((entries) => {
      const incoming = entries
        .filter(e => e.isIntersecting && !e.target.classList.contains('is-visible'))
        .map(e => e.target);

      if (incoming.length) {
        animateListItems(incoming);
        incoming.forEach(item => this._listObserver.unobserve(item));
      }
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px 50px 0px'
    });

    document.querySelectorAll('.client-list li').forEach(item => {
      this._listObserver.observe(item);
    });

    const preview = document.querySelector('.client-preview-image');
    const previewVideo = document.querySelector('.client-preview-video');
    const positions = ['pos-bottom-right', 'pos-center', 'pos-bottom-left', 'pos-top-right', 'pos-top-left'];
    let posIndex = 0;

    if (window.matchMedia('(hover: hover)').matches) {
      document.querySelectorAll('a[data-image]').forEach(link => {
        link.addEventListener('mouseenter', () => {
          const pos = positions[posIndex];
          posIndex = (posIndex + 1) % positions.length;

          if (link.dataset.video) {
            preview.classList.remove('is-visible');
            previewVideo.classList.remove(...positions);
            previewVideo.classList.add(pos, 'is-visible');
            previewVideo.src = link.dataset.video;
            previewVideo.play();
          } else {
            previewVideo.classList.remove('is-visible');
            previewVideo.pause();
            preview.classList.remove(...positions);
            preview.classList.add(pos);
            preview.src = link.dataset.image;
            preview.classList.add('is-visible');
          }
        });

        link.addEventListener('mouseleave', () => {
          preview.src = '';
          preview.classList.remove('is-visible');
          previewVideo.pause();
          previewVideo.src = '';
          previewVideo.classList.remove('is-visible');
        });
      });

      document.querySelectorAll('.has-children').forEach(item => {
        item.addEventListener('mouseenter', () => {
          item.classList.add('is-open');
        }, { once: true });
      });
    }

    gsap.registerPlugin(ScrollTrigger);

    this._pinTrigger = ScrollTrigger.create({
      trigger: '.client-list-canvas',
      endTrigger: '.site-footer',
      pin: true,
      start: 'top +=110',
      end: 'top 98%',
    });
  }

  onLeave() {
    if (this._listObserver) {
      this._listObserver.disconnect();
      this._listObserver = null;
    }

    if (this._pinTrigger) {
      this._pinTrigger.kill();
      this._pinTrigger = null;
    }
  }
}

/* *******************************************************
 * PEOPLE PAGE TEMPLATE
 * ******************************************************* */
export class PeopleRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    updateBodyClass(this.page);
    setManualScrollRestoration();
    sitewideInit();

    this._observer = new IntersectionObserver((entries) => {
      const incoming = entries
        .filter(e => e.isIntersecting && !e.target.classList.contains('is-visible'))
        .map(e => e.target);

      if (incoming.length) {
        animateCards(incoming);
        incoming.forEach(card => this._observer.unobserve(card));
      }
    }, {
      threshold: 0,
      rootMargin: '0px 0px 90px 0px'
    });

    document.querySelectorAll('.work-grid-item').forEach(card => {
      this._observer.observe(card);

      const video = card.querySelector('video');
      if (!video) return;
      card.addEventListener('mouseenter', () => video.play());
      card.addEventListener('mouseleave', () => video.pause());
    });

    initVideos();
  }

  onLeave() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }

    cleanupVideos();
  }
}

/* *******************************************************
 * PROJECT SINGLE PAGE
 * ******************************************************* */
export class ProjectRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    gsap.registerPlugin(ScrollTrigger);
    setManualScrollRestoration();
    updateBodyClass(this.page);
    sitewideInit();


    // MOBILE TRANSITION
    createTimeline()
    .add(isMobile ? '.project-header__mobile-play--inner' : [], {
      opacity: [0, 1],
      duration: 400,
      easing: EASE,
    }, 250)
    .add(isMobile ? '.project-meta-info' : [], {
      opacity: [0, 1],
      translateY: [30, 0],
      duration: 400,
      delay: stagger(150),
      easing: EASE,
      onComplete: () => {
        animate('.project-video', {opacity: [0, 1]});
      }
    }, 250);

    if (!isMobile) {
      gsap.set('.project-meta-info', { opacity: 1 });

      gsap.timeline({ defaults: { ease: 'power3.out' } })
      .fromTo('.project-video',
        { opacity: 0 },
        { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'power2.out', onComplete: () => {
          
          gsap.set('.project-header__mobile-play--inner', { opacity: 1 });
        } },
        0.45
      );

      const infoEl = document.querySelector('.project-description__info');
      const infoInView = infoEl && infoEl.getBoundingClientRect().top < window.innerHeight;

      gsap.fromTo('.project-description__info .col-lg-6',
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.4,
          stagger: 0.15,
          ease: 'power2.out',
          delay: infoInView ? 1.0 : 0,
          scrollTrigger: {
            trigger: '.project-description__info',
            start: 'top 95%',
            toggleActions: 'play none none none',
            // markers: true,
          },
        }
      );
      
    }

    // Related 
    gsap.fromTo('.project-related__card',
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.4,
        // delay: 0.25,
        // stagger: 0.25,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: '.project-related__card',
          start: 'top 90%',
          end: 'bottom +3000',
          toggleActions: 'play none none none',
          // markers: true,

        },
      }
    );

    initScrollAnimations({ skipSelector: isMobile ? '.project-meta-info' : null });
    initProjectPlayer();
  }
}


/* *******************************************************
 * HOME PAGE OLD
 * ******************************************************* */
export class HomepageOldRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    // initCursorText('[data-cursor-text]');
    updateBodyClass(this.page);

    if (!window.isTaxiTransition) {
      initHomepageSplide(this.page, runHomepageAni);
    } else {
      initHomepageSplide(this.page);
      animate('.hp-card', {
        opacity: [0, 1],
        scale: [0.62, 1],
        duration: 500,
        delay: 700,
        easing: EASE,
      });
    }
  }

  onLeave() {
    const cursorText = document.getElementById('cursor-text');
    cursorText.textContent = '';
  }
}


/* *******************************************************
 * ABOUT PAGE
 * ******************************************************* */
export class AboutRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    updateBodyClass(this.page);
    runAboutAni();
  }
}



/* OLD: TO BE DELETED
export class ProjectOLdRenderer extends Renderer {
  initialLoad() {
    this.onEnter()
    this.onEnterCompleted()
  }

  onEnter() {
    setManualScrollRestoration();
    updateBodyClass(this.page);

    createTimeline()
    .add('.project-header', {
      opacity: [0, 1],
      translateY: [80, 0],
      scale: [0.8, 1],
      duration: 450,
      delay: 450,
      easing: 'easeOutExpo',
    }, 0)
    // initCursorText('[data-cursor-text]');
/*
    gsap.registerPlugin(ScrollTrigger);

    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    initScrollAnimations({ skipSelector: isMobile ? '.project-meta-info' : null });

    createTimeline()
    add('.project-header', {
      opacity: [0, 1],
      translateY: [80, 0],
      scale: [0.8, 1],
      duration: 450,
      delay: 450,
      easing: 'easeOutExpo',
    }, 0)
    .add(isMobile ? '.project-meta-info' : [], {
      opacity: [0, 1],
      scale: [0.62, 1],
      translateY: [40, 0],
      translateX: [-20, 0],
      duration: 400,
      delay: stagger(100),
      easing: EASE,
    }, 600);

    gsap.from('.project-info-line', {
      scaleX: 0,
      ease: 'power2.in',
      scrollTrigger: { trigger: '.project-info-line', start: 'top 97%' },
    });

    // initProjectPlayer();
    *
  }
}
*/


/* *******************************************************
 * Functions
 * ******************************************************* */

function animateCards(cards) {
  cards.forEach(card => { card.style.opacity = '0'; });

  const tl = createTimeline();

  cards.forEach((card, i) => {
    const offset = i * 70;
    tl.add(card, { scale: [0.62, 1], duration: 600, easing: EASE }, offset)
      .add(card, { opacity: [0, 1], duration: 500, easing: EASE }, offset + 60);
  });
}

function animateListItems(items) {
  items.forEach(item => {
    item.style.opacity = '0';
    item.style.transform = 'translateY(80px) scale(0.62)';
    item.style.transformOrigin = 'bottom left';
  });

  const tl = createTimeline();

  items.forEach((item, i) => {
    const offset = i * 30;
    tl.add(item, { scale: [0.62, 1], translateY: [80, 0], duration: 600, easing: EASE }, offset)
      .add(item, { opacity: [0, 1], duration: 500, easing: EASE }, offset + 60);
  });
}

function runHomepageAni() {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  const frameTopTo = isMobile ? 80 : 110;
  const hpDelay = 700;
  const hpDuration = 500;

  utils.set('.frame-strip.left',  { translateX: -25 });
  utils.set('.frame-strip.right', { translateX: 25 });

  createTimeline()
  .add('.frame-strip.top', {
    translateY: [0, frameTopTo],
    duration: hpDuration,
    delay: hpDelay,
    onComplete: () => setTimeout(() => {
      const el = document.querySelector('.frame-strip.top');
      el.removeAttribute('style');
      el.classList.add('is-complete');
    }, 250),
  }, 1000)
  .add('.frame-strip.left',   { translateX: [-25, 0], duration: hpDuration, delay: hpDelay, easing: EASE }, 1000)
  .add('.frame-strip.right',  { translateX: [25, 0],  duration: hpDuration, delay: hpDelay, easing: EASE }, 1000)
  .add('.frame-strip.bottom', { translateY: [0, -25], duration: hpDuration, delay: hpDelay, easing: EASE }, 1000)
  .add('.loading-screen', {
    opacity: [1, 0],
    duration: 400,
    delay: hpDelay,
    easing: EASE,
    onComplete: () => {
      document.querySelector('.loading-screen').style.visibility = 'hidden';
    },
  }, 800)
  .add('.js-animated-header', { opacity: [0, 1], duration: hpDuration, delay: hpDelay, easing: EASE }, 1200)
  .add('.hp-card',            { opacity: [0, 1], scale: [0.62, 1], duration: 500, delay: 700, easing: EASE }, 1100);
}

function runAboutAni() {
  createTimeline({ defaults: { easing: EASE } })
  .add('.about-text', {
    opacity: [0, 1],
    translateY: [80, 0],
    translateX: [-20, 0],
    scale: [0.8, 1],
    duration: 750,
  }, 0)
  .add('.about-footer__image', {
    opacity: [0, 1],
    scale: [0.62, 1],
    translateY: [40, 0],
    duration: 500,
    easing: EASE,
  }, 300)
  .add('.about-footer__contact div', {
    opacity: [0, 1],
    translateY: [120, 0],
    duration: 550,
    delay: stagger(70),
    easing: EASE,
  }, 400);
}

const playedPlayers = new WeakSet();

function showPlayOverlay(player) {
  if (playedPlayers.has(player)) return;

  const card = document.querySelector('.splide__slide.is-active:not(.is-clone) .hp-card');
  const overlay = card?.querySelector('.hp-card__overlay');
  if (!overlay) return;

  overlay.classList.add('needs-play');

  function onOverlayClick(e) {
    e.preventDefault();
    e.stopPropagation();
    overlay.classList.remove('needs-play');
    overlay.removeEventListener('click', onOverlayClick);
    playedPlayers.add(player);
    player.play();
  }

  overlay.addEventListener('click', onOverlayClick);
}

function initHomepageSplide(page, onReady) {
  const staticOverlay = document.querySelector('.static-overlay');

  const splide = new Splide('.splide', {
    arrows: false,
    pagination: true,
    type: 'fade',
    rewind: true,
  });

  splide.mount();

  document.querySelector('.hp-arrow-prev').addEventListener('click', () => splide.go('<'));
  document.querySelector('.hp-arrow-next').addEventListener('click', () => splide.go('>'));

  const players = {};

  page.querySelectorAll('.splide__slide:not(.is-clone) iframe').forEach((iframe, index) => {
    players[index] = new Player(document.querySelector(`#${iframe.id}`), {
      muted: true,
      controls: false,
      loop: false,
    });

    players[index].pause();

    players[index].on('ended', () => splide.go('>'));
  });

  function revealHomepage() {
    players[0].off('play');
    staticOverlay.classList.remove('is-visible', 'is-visible-important');
    if (onReady) onReady();

    // Prime remaining videos: play briefly to force Vimeo to buffer, then reset
    Object.entries(players).forEach(([index, player]) => {
      if (index === '0') return;
      player.play().then(() => {
        player.pause();
        player.setCurrentTime(0);
      });
    });
  }

  let autoplayBlocked = false;
  const overlayShown = new Set();

  players[0].ready().then(() => {
    players[0].play().catch(() => {
      autoplayBlocked = true;
      revealHomepage();
      overlayShown.add(0);
      showPlayOverlay(players[0]);
    });
  });

  players[0].on('play', revealHomepage);

  splide.on('move', (newIndex, prevIndex) => {
    staticOverlay.classList.add('is-visible');

    players[prevIndex]?.pause();
    players[newIndex]?.play();

    if (autoplayBlocked) {
      setTimeout(() => {
        staticOverlay.classList.remove('is-visible');
        if (!overlayShown.has(newIndex)) {
          overlayShown.add(newIndex);
          showPlayOverlay(players[newIndex]);
        }
      }, 250);
    } else {
      const minDelay = new Promise(resolve => setTimeout(resolve, 250));
      const videoPlaying = new Promise(resolve => {
        players[newIndex].on('play', () => {
          players[newIndex].off('play');
          resolve();
        });
      });

      Promise.all([minDelay, videoPlaying]).then(() => {
        staticOverlay.classList.remove('is-visible');
      });
    }
  });
}

function initProjectPlayer() {
  const iframe = document.querySelector('.embed-container iframe');
  const coverImage = document.querySelector('.embed-container__image');
  const player = new Player(iframe);

  const playBtn      = document.getElementById('playPauseBtn');
  const muteBtn      = document.getElementById('muteBtn');
  const fullScreenBtn = document.getElementById('fullScreenBtn');
  const playOverlay  = document.querySelector('.embed-container__play-overlay');
  const scrubBar     = document.getElementById('scrubBar');
  const scrubFill    = document.getElementById('scrubFill');

  let duration = 0;
  let rafId    = null;
  let isPlaying = false;
  let isMute    = true;
  let isBuffered = false;

  const videoControls = document.querySelector('.video-controls');
  const projectVideo  = document.querySelector('.project-video');
  let hideTimer = null;

  function showControls() {
    if (!videoControls) return;
    videoControls.style.opacity = '1';
    videoControls.style.pointerEvents = 'auto';
  }

  function scheduleHide() {
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => {
      if (!videoControls) return;
      videoControls.style.opacity = '0';
      videoControls.style.pointerEvents = 'none';
    }, 2500);
  }

  projectVideo?.addEventListener('mouseenter', () => {
    showControls();
    scheduleHide();
  });

  projectVideo?.addEventListener('mousemove', () => {
    showControls();
    scheduleHide();
  });

  projectVideo?.addEventListener('mouseleave', () => {
    clearTimeout(hideTimer);
    videoControls.style.opacity = '0';
    videoControls.style.pointerEvents = 'none';
  });

  scheduleHide();

  function togglePlay() {
    if (isPlaying) player.pause();
    else player.play();
  }

  const playOnMobileBtn = document.getElementById('playOnMobileBtn');

  player.play().catch(() => {
    console.log('autoplay blocked');
  }).then(() => {
    // if mobile pause
    if (window.innerWidth < MOBILE_BREAKPOINT) {
      player.pause();
    }

    // fade out cover image
    coverImage.style.opacity = 0;
    coverImage.style.display = 'none';
  });

  player.on('bufferend', () => {
    playOnMobileBtn.classList.add('is-loaded');
  });

  playOnMobileBtn.addEventListener('click', () => {
    player.play();
    player.requestFullscreen();    
  });


  // ADDED
  function togglePlay() {
    if (isPlaying) player.pause();
    else player.play();
  }

  fullScreenBtn.addEventListener('click', () => {
    player.requestFullscreen();
    if (!isPlaying) {
      player.play();
    }
  });

  playBtn.addEventListener('click', togglePlay);
  playOverlay.addEventListener('click', togglePlay);

  player.getDuration().then(d => { duration = d; });


  muteBtn.addEventListener('click', () => {
    isMute = !isMute;
    player.setMuted(isMute);
    muteBtn.classList.toggle('muted', !isMute);
  });
  
  function updateScrub() {
    player.getCurrentTime().then(seconds => {
      // console.log(seconds);
      if (duration === 0) return;
      scrubFill.style.width = `${(seconds / duration) * 100}%`;
    });
    rafId = requestAnimationFrame(updateScrub);
  }

  scrubBar.addEventListener('click', (e) => {
    if (duration === 0) return;
    const rect = scrubBar.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    player.setCurrentTime(ratio * duration);
    scrubFill.style.width = `${ratio * 100}%`;
  });

  player.on('play', () => {
    isPlaying = true;
    updateScrub();
    playBtn.classList.remove('paused');
  });

  player.on('pause', () => {
    isPlaying = false;
    cancelAnimationFrame(rafId);
    playBtn.classList.add('paused');
  });

  player.on('ended', () => {
    isPlaying = false;
    cancelAnimationFrame(rafId);
    playBtn.classList.add('paused');
  });
}


/* *******************************************************
 * People page — video popup
 * ******************************************************* */

let _videoCleanup = null;

function initVideos() {
  const popup   = document.getElementById('video-popup');
  const player  = document.getElementById('vimeo-player');

  function closePopup() {
    popup.classList.remove('visible');
    player.src = '';
  }

  function onKeydown(e) {
    if (e.key === 'Escape') closePopup();
  }

  function onPopupClick(e) {
    if (e.target === popup || e.target.closest('.popup-content')) closePopup();
  }

  document.querySelectorAll('.js-play-video').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      player.src = btn.dataset.videoUrl;
      popup.classList.add('visible');
    });
  });

  document.querySelector('.close-popup')?.addEventListener('click', (e) => { e.preventDefault(); closePopup(); });
  popup.addEventListener('click', onPopupClick);
  document.addEventListener('keydown', onKeydown);

  // Store cleanup so onLeave can remove document-level listeners
  _videoCleanup = () => {
    popup.removeEventListener('click', onPopupClick);
    document.removeEventListener('keydown', onKeydown);
    closePopup();
    _videoCleanup = null;
  };
}

function cleanupVideos() {
  if (_videoCleanup) _videoCleanup();
}


/* *******************************************************
 * Scroll reveal
 * ******************************************************* */

const PRESETS = {
  fade:  { opacity: [0, 1] },
  fadeUp:  { opacity: [0, 1], translateY: [24, 0] },
  special:  { scale: [0.62, 1], translateY: [80, 0], translateX: [-40, 0], opacity: [0, 1] },
  special2: { scale: [0.62, 1], translateY: [40, 0], opacity: [0, 1] },
  special3: { opacity: [0, 1], translateY: [60, 0] },
};

function initScrollAnimations({
  selector     = '[data-animate]',
  skipSelector = null,
  threshold    = 0.15,
  once         = true,
} = {}) {
  const observer = new IntersectionObserver((entries) => {
    const visible = entries.filter(e => e.isIntersecting);

    // Group by parent so siblings stagger together
    const groups = new Map();
    visible.forEach(entry => {
      const key = entry.target.parentElement ?? 'root';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(entry.target);
    });

    groups.forEach(els => {
      els.forEach((el, i) => {
        const ds        = el.dataset;
        const preset    = PRESETS[ds.animate] ?? PRESETS.fadeUp;
        const duration  = Number(ds.duration ?? 600);
        const easing    = ds.easing ?? 'easeOutExpo';
        const staggerGap = Number(ds.stagger ?? el.parentElement?.dataset.stagger ?? 80);
        const baseDelay = Number(ds.delay ?? 0);

        el.style.transform = '';
        el.style.filter    = '';

        animate(el, {
          ...preset,
          duration,
          easing,
          delay: baseDelay + i * staggerGap,
        });

        if (once) observer.unobserve(el);
      });
    });
  }, { threshold });

  document.querySelectorAll(selector).forEach(el => {
    if (skipSelector && el.matches(skipSelector)) return;
    observer.observe(el);
  });

  return observer;
}



/*
Homepage v2

onEnter() {
    updateBodyClass(this.page);
    gsap.registerPlugin(ScrollTrigger);

    document.querySelectorAll('.hp-scroll-item').forEach(item => {
      const video = item.querySelector('video');
      if (!video) return;
      item.addEventListener('mouseenter', () => { video.play(); video.classList.add('is-playing'); });
      item.addEventListener('mouseleave', () => { video.pause(); video.classList.remove('is-playing'); });
    });

    const hpScroll = document.querySelector('.hp-scroll');
    if (!hpScroll) return;

    const cards = gsap.utils.toArray('.hp-scroll-item');

    this._pinTriggers = cards.flatMap((card) => {
      const scaleAnim = gsap.fromTo(card,
        { scale: 1, yPercent: 0 },
        {
          scale: 0.6,
          yPercent: 100,
          ease: 'none',
          scrollTrigger: {
            trigger: card,
            start: 'top 80',
            end: 'bottom 80',
            scrub: true,
            markers: true,
            onLeave: () => {
              gsap.to(card, {opacity: 0, duration: 0});
            },
            onEnterBack: () => {
              gsap.to(card, {opacity: 1, duration: 0});
            }
          },
        }
      );

      

      return [scaleAnim.scrollTrigger];
    });
  }

  onLeave() {

    if (this._pinTriggers) {
      this._pinTriggers.forEach(t => t.kill());
      this._pinTriggers = null;
    }
  }

  */